import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  useClientQuery,
  useUpdateClientWhitelistedDomainsMutation,
} from "@graphql/clients/clients.g";

import {
  DomainChip,
  DomainChipInput,
} from "@components/Inputs/DomainChipInput/DomainChipInput";

import { Button } from "@design-system/Button/Button";
import { Card } from "@design-system/Card/Card";
import { FieldGroup } from "@design-system/FieldGroup/FieldGroup";
import { Text } from "@design-system/Text/Text";
import { getWhitelistedDomainError } from "@utils/whitelistedDomain";

type Props = {
  clientId: string;
};

const normalizeDomain = (domain: string) => domain.trim().toLowerCase();

const toLocalChip = (domain: string): DomainChip => ({
  id: `local:${normalizeDomain(domain)}`,
  domain: domain.trim(),
});

export const WhitelistedDomainsCard = ({ clientId }: Props) => {
  const clientQuery = useClientQuery({ clientId });
  const updateMutation = useUpdateClientWhitelistedDomainsMutation();

  const serverDomains = useMemo<DomainChip[]>(
    () =>
      clientQuery.data?.client?.whitelistedDomains?.flatMap((domain) =>
        domain?.id && domain.domain
          ? [{ id: domain.id, domain: domain.domain }]
          : [],
      ) ?? [],
    [clientQuery.data?.client?.whitelistedDomains],
  );

  const [localDomains, setLocalDomains] = useState<DomainChip[] | null>(null);
  const [draft, setDraft] = useState("");
  const [invalidMessages, setInvalidMessages] = useState<Record<string, string>>(
    {},
  );

  useEffect(() => {
    if (localDomains === null && clientQuery.data?.client) {
      setLocalDomains(serverDomains);
    }
  }, [clientQuery.data?.client, localDomains, serverDomains]);

  const domains = localDomains ?? serverDomains;

  const hasDraft = draft.trim().length > 0;
  const isDirty =
    hasDraft ||
    (localDomains !== null &&
      JSON.stringify(localDomains.map((domain) => normalizeDomain(domain.domain)).sort()) !==
        JSON.stringify(
          serverDomains.map((domain) => normalizeDomain(domain.domain)).sort(),
        ));

  const markInvalid = (chips: DomainChip[]) => {
    const nextErrors: Record<string, string> = {};
    for (const chip of chips) {
      const error = getWhitelistedDomainError(chip.domain);
      if (error) {
        nextErrors[chip.id] = error;
      }
    }
    setInvalidMessages(nextErrors);
    return nextErrors;
  };

  const handleAdd = (domain: string) => {
    const chip = toLocalChip(domain);
    setLocalDomains((current) => {
      const existing = current ?? serverDomains;
      if (
        existing.some(
          (item) => normalizeDomain(item.domain) === normalizeDomain(domain),
        )
      ) {
        return existing;
      }
      return [...existing, chip];
    });
    const error = getWhitelistedDomainError(domain);
    setInvalidMessages((current) => {
      if (!error) {
        return Object.fromEntries(
          Object.entries(current).filter(([id]) => id !== chip.id),
        );
      }
      return { ...current, [chip.id]: error };
    });
  };

  const handleRemove = (id: string) => {
    setLocalDomains((current) =>
      (current ?? serverDomains).filter((chip) => chip.id !== id),
    );
    setInvalidMessages((current) =>
      Object.fromEntries(Object.entries(current).filter(([chipId]) => chipId !== id)),
    );
  };

  const handleUpdate = async () => {
    const pendingDraft = draft.trim();
    const nextDomains = [...domains];
    if (
      pendingDraft &&
      !nextDomains.some(
        (chip) => normalizeDomain(chip.domain) === normalizeDomain(pendingDraft),
      )
    ) {
      nextDomains.push(toLocalChip(pendingDraft));
    }
    setDraft("");
    setLocalDomains(nextDomains);

    markInvalid(nextDomains);

    try {
      const result = await updateMutation.mutateAsync({
        input: {
          clientId,
          domains: nextDomains.map((chip) => chip.domain),
        },
      });
      const errors =
        result.updateClientWhitelistedDomains?.errors?.filter(
          (error) => error != null,
        ) ?? [];

      if (errors.length > 0) {
        const nextMessages: Record<string, string> = {};
        const unmatched: string[] = [];

        for (const error of errors) {
          const message = error.message || "Enter a valid domain name.";
          const chip = nextDomains.find(
            (item) =>
              item.domain === error.field ||
              normalizeDomain(item.domain) === normalizeDomain(error.field || ""),
          );
          if (chip) {
            nextMessages[chip.id] = message;
          } else if (message) {
            unmatched.push(message);
          }
        }

        setInvalidMessages(nextMessages);
        toast.error(
          unmatched[0] || "Fix the highlighted domains before saving.",
        );
        return;
      }

      toast.success("Whitelisted domains updated");
      const refreshed = await clientQuery.refetch();
      const saved =
        refreshed.data?.client?.whitelistedDomains?.flatMap((domain) =>
          domain?.id && domain.domain
            ? [{ id: domain.id, domain: domain.domain }]
            : [],
        ) ?? [];
      setLocalDomains(saved);
      setInvalidMessages({});
    } catch {
      toast.error("Unable to save domains");
    }
  };

  const isSaving = updateMutation.isPending;

  return (
    <Card>
      <Card.Header>
        <Card.Header.Title>Whitelisted email domains</Card.Header.Title>
      </Card.Header>
      <Card.Body gap={12}>
        <Text size={12} color="textSecondary">
          Providing a whitelisted domain enables alerts when a user signs
          in with an email that does not match any domain listed below.
        </Text>
        <FieldGroup
          label="Allowed root domains"
          error={
            Object.keys(invalidMessages).length > 0
              ? "Highlighted domains are invalid."
              : undefined
          }
        >
          <DomainChipInput
            domains={domains}
            draft={draft}
            onDraftChange={setDraft}
            onAdd={handleAdd}
            onRemove={handleRemove}
            invalidIds={Object.keys(invalidMessages)}
            invalidMessages={invalidMessages}
            disabled={isSaving}
          />
        </FieldGroup>
        <Button
          isLoading={isSaving}
          disabled={!isDirty || isSaving}
          onClick={handleUpdate}
        >
          Update
        </Button>
      </Card.Body>
    </Card>
  );
};
